import { useEffect, useMemo, useRef, useState } from 'react'

import {
  alFallarLaLlave,
  cargarGoogleMaps,
  hayLlaveDeGoogle,
  llaveDeGoogleRechazada,
  ESTILO_MAPA,
} from '@/lib/mapas/google'

import estilos from './MapaRuta.module.css'

/**
 * LA RUTA DEL DÍA EN EL MAPA.
 *
 * Une, en orden, las paradas de un recorrido: primero la 1, luego la 2… con una
 * línea entre ellas. Sirve igual para que el técnico vea su día en el teléfono
 * y para que la oficina vea por dónde va a andar cada quien. No inventa el
 * orden: recibe las paradas ya ordenadas (por hora programada) y las dibuja tal
 * cual, numeradas.
 *
 * Es tonto a propósito: no consulta nada. Quien lo usa le pasa las paradas.
 */

export type Parada = {
  lat: number
  lng: number
  /** Lo que va en la burbuja: normalmente el número de la orden. */
  etiqueta: string
  /** Segunda línea: cliente, hora… opcional. */
  sub?: string | null
  /** En ejecución ahora: se resalta. */
  activa?: boolean
}

const CARTAGO = { lat: 4.7465, lng: -75.9115 }

export function MapaRuta({ paradas, altura = 260 }: { paradas: Parada[]; altura?: number }) {
  const caja = useRef<HTMLDivElement>(null)
  const mapa = useRef<google.maps.Map | null>(null)
  const dibujado = useRef<google.maps.MVCObject[]>([])
  const [error, setError] = useState<string | null>(
    llaveDeGoogleRechazada() ? 'El mapa no está disponible en este momento.' : null,
  )

  // Google no falla la promesa cuando rechaza la llave: carga el script y
  // pinta su cartel dentro del div. Este es el aviso que sí llega.
  useEffect(
    () => alFallarLaLlave(() => setError('El mapa no está disponible en este momento.')),
    [],
  )

  // Solo las que tienen punto: sin coordenadas no se pueden poner en el mapa.
  const conPunto = useMemo(
    () => paradas.filter((p) => p.lat != null && p.lng != null),
    [paradas],
  )

  useEffect(() => {
    if (!hayLlaveDeGoogle()) {
      setError('Falta la llave de Google Maps.')
      return
    }
    let vivo = true

    void cargarGoogleMaps()
      .then((api) => {
        if (!vivo || !caja.current) return
        const maps = window.google.maps

        if (!mapa.current) {
          mapa.current = new api.maps.Map(caja.current, {
            styles: ESTILO_MAPA,
            center: CARTAGO,
            zoom: 13,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            clickableIcons: false,
          })
        }

        // Se redibuja entero: son pocas paradas.
        dibujado.current.forEach((o) => (o as unknown as { setMap: (m: null) => void }).setMap(null))
        dibujado.current = []

        if (conPunto.length === 0) return

        const limites = new maps.LatLngBounds()
        const camino: google.maps.LatLngLiteral[] = []

        conPunto.forEach((p, i) => {
          const posicion = { lat: Number(p.lat), lng: Number(p.lng) }
          camino.push(posicion)
          limites.extend(posicion)

          const marca = new maps.Marker({
            position: posicion,
            map: mapa.current,
            // El número de parada, para leer el orden de un vistazo.
            label: { text: String(i + 1), color: '#fff', fontWeight: '700', fontSize: '12px' },
            title: `${i + 1}. ${p.etiqueta}${p.sub ? ' · ' + p.sub : ''}`,
            icon: {
              path: maps.SymbolPath.CIRCLE,
              scale: 13,
              fillColor: p.activa ? '#DB2777' : '#2D0177',
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 2.5,
            },
            zIndex: p.activa ? 999 : undefined,
          })

          const ficha = new maps.InfoWindow({
            content: `<div style="font-family:Inter,system-ui,sans-serif;line-height:1.4">
              <b style="color:#2D0177">${i + 1}. ${p.etiqueta}</b>
              ${p.sub ? `<div style="color:#47406B;font-size:12px">${p.sub}</div>` : ''}
            </div>`,
          })
          marca.addListener('click', () => ficha.open({ map: mapa.current!, anchor: marca }))
          dibujado.current.push(marca)
        })

        // La línea que une las paradas en orden: eso es la ruta.
        if (camino.length > 1) {
          const linea = new maps.Polyline({
            path: camino,
            map: mapa.current,
            geodesic: true,
            strokeColor: '#2D0177',
            strokeOpacity: 0.75,
            strokeWeight: 4,
          })
          dibujado.current.push(linea)
        }

        if (conPunto.length > 1) mapa.current.fitBounds(limites, 40)
        else {
          mapa.current.setCenter(limites.getCenter())
          mapa.current.setZoom(16)
        }
      })
      .catch(() => setError('No se pudo cargar el mapa.'))

    return () => {
      vivo = false
    }
  }, [conPunto])

  if (error) return <p className={estilos.error}>{error}</p>

  return (
    <div className={estilos.marco}>
      <div ref={caja} className={estilos.mapa} style={{ height: altura }} />
      {conPunto.length < paradas.length && (
        <p className={estilos.nota}>
          {paradas.length - conPunto.length} sin ubicar todavía (falta el punto de la dirección).
        </p>
      )}
    </div>
  )
}
